// src/components/TeamItemsPage/TeamItemCard.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './TeamItemCard.module.css';
import { 
  FaEdit, FaTrash, FaFlag, FaCalendarAlt, FaCheckCircle, FaClock, 
  FaEye, FaComments, FaCheck, FaUser, FaFutbol, FaTag, FaExclamationTriangle,
  FaPlay, FaHourglass, FaFireAlt
} from 'react-icons/fa';
import { format, differenceInCalendarDays, isBefore, startOfDay, isToday, isTomorrow } from 'date-fns';

const TeamItemCard = ({ 
  item, 
  onEdit, 
  onDelete, 
  onMarkComplete,
  onQuickView,
  canManage = false,
  canComplete = false, // Allows assigned users to complete their own tasks
  isHighlighted = false,
  isSelected = false,
  onToggleSelect,
  bulkMode = false,
  urgencyLevel = 'none',
  commentCount = 0
}) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompletedAnimation, setShowCompletedAnimation] = useState(false);

  const handleCardClick = (e) => {
    // Prevent triggering actions when clicking on buttons or checkbox
    if (e.target.closest('button') || e.target.closest('input[type="checkbox"]')) return;
    
    if (bulkMode && canManage) {
      onToggleSelect?.();
    } else if (onQuickView) {
      onQuickView();
    }
  };

  const handleMarkComplete = async (e) => {
    e.stopPropagation();
    setIsCompleting(true);
    
    try {
      await onMarkComplete?.();
      setShowCompletedAnimation(true);
      setTimeout(() => setShowCompletedAnimation(false), 2000);
    } catch (error) {
      console.error('Error completing task:', error);
    } finally {
      setIsCompleting(false);
    }
  };

  // Calculate due date info
  const today = startOfDay(new Date());
  const dueDate = item.dueDate ? startOfDay(new Date(item.dueDate)) : null;
  
  let dueDateInfo = { text: 'No due date', class: '', icon: FaCalendarAlt, urgent: false };
  
  if (dueDate) {
    const daysLeft = differenceInCalendarDays(dueDate, today);
    const formattedDate = format(new Date(item.dueDate), 'MMM d');
    
    if (item.status === 'Done') {
      dueDateInfo = { text: formattedDate, class: styles.dueDone, icon: FaCheckCircle, urgent: false };
    } else if (isBefore(dueDate, today)) {
      const daysOverdue = Math.abs(daysLeft);
      dueDateInfo = { 
        text: `${daysOverdue}d overdue`, 
        class: styles.dueOverdue, 
        icon: FaExclamationTriangle, 
        urgent: true 
      };
    } else if (isToday(dueDate)) {
      dueDateInfo = { text: 'Today', class: styles.dueToday, icon: FaFireAlt, urgent: true };
    } else if (isTomorrow(dueDate)) {
      dueDateInfo = { text: 'Tomorrow', class: styles.dueTomorrow, icon: FaHourglass, urgent: true };
    } else if (daysLeft <= 7) {
      dueDateInfo = { text: `${daysLeft}d left`, class: styles.dueSoon, icon: FaClock, urgent: false };
    } else {
      dueDateInfo = { text: formattedDate, class: styles.dueFuture, icon: FaCalendarAlt, urgent: false };
    }
  }

  // Get priority styling
  const getPriorityInfo = (priority) => {
    switch (priority) {
      case 'High':
        return { class: styles.priorityHigh, icon: FaFlag, color: '#ef4444' };
      case 'Medium':
        return { class: styles.priorityMedium, icon: FaFlag, color: '#f59e0b' };
      case 'Low':
        return { class: styles.priorityLow, icon: FaFlag, color: '#10b981' };
      default:
        return { class: styles.priorityDefault, icon: FaFlag, color: '#6b7280' };
    }
  };

  const priorityInfo = getPriorityInfo(item.priority);

  // Get status styling
  const getStatusInfo = (status) => {
    switch (status) {
      case 'To Do':
        return { class: styles.statusTodo, icon: FaClock, text: 'To Do' };
      case 'In Progress':
        return { class: styles.statusInProgress, icon: FaPlay, text: 'In Progress' };
      case 'Done':
        return { class: styles.statusDone, icon: FaCheckCircle, text: 'Completed' };
      default:
        return { class: styles.statusDefault, icon: FaClock, text: status };
    }
  };

  const statusInfo = getStatusInfo(item.status);

  // Calculate progress (mock - based on status)
  const getProgressPercentage = () => {
    switch (item.status) {
      case 'To Do': return 0;
      case 'In Progress': return 50;
      case 'Done': return 100;
      default: return 0;
    }
  };

  const progress = getProgressPercentage();

  // Get urgency class for card border
  const getUrgencyClass = () => {
    if (item.status === 'Done') return styles.cardDone;
    if (dueDateInfo.urgent && item.priority === 'High') return styles.cardCritical;
    if (dueDateInfo.class === styles.dueOverdue) return styles.cardOverdue;
    if (dueDateInfo.class === styles.dueToday) return styles.cardToday;
    if (item.priority === 'High') return styles.cardHighPriority;
    return '';
  };

  const DueDateIcon = dueDateInfo.icon;
  const StatusIcon = statusInfo.icon;
  const PriorityIcon = priorityInfo.icon;

  return (
    <div 
      className={`
        ${styles.card} 
        ${getUrgencyClass()} 
        ${isHighlighted ? styles.highlightedCard : ''} 
        ${isSelected ? styles.selectedCard : ''} 
        ${bulkMode ? styles.bulkModeCard : ''}
        ${showCompletedAnimation ? styles.completedAnimation : ''}
      `} 
      data-item-id={item._id}
      onClick={handleCardClick}
      role="article"
      aria-label={`Assignment: ${item.title}`}
    >
      {/* Priority Indicator Bar */}
      <div 
        className={styles.priorityBar} 
        style={{ backgroundColor: priorityInfo.color }}
        aria-hidden="true"
      />

      {/* Main Card Content */}
      <div className={styles.cardContent}>
        {/* Header Row */}
        <div className={styles.headerRow}>
          {/* Bulk Selection Checkbox */}
          {bulkMode && canManage && (
            <div className={styles.selectionCheckbox}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={onToggleSelect}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${item.title}`}
              />
            </div>
          )}

          {/* Priority Badge */}
          <span className={`${styles.priorityBadge} ${priorityInfo.class}`}>
            <PriorityIcon /> {item.priority}
          </span>

          {/* Title */}
          <h3 className={styles.cardTitle}>{item.title}</h3>

          {/* Actions */}
          {canManage && !bulkMode && (
            <div className={styles.cardActions}>
              <button 
                onClick={(e) => { e.stopPropagation(); onQuickView?.(); }} 
                className={styles.actionBtn} 
                title="Quick View"
                aria-label="Quick view"
              >
                <FaEye />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); onEdit(); }} 
                className={styles.actionBtn} 
                title="Edit"
                aria-label="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className={`${styles.actionBtn} ${styles.deleteBtn}`}
                title="Delete"
                aria-label="Delete"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        {item.description && (
          <p className={styles.description}>
            {item.description.length > 120 
              ? `${item.description.substring(0, 120)}...` 
              : item.description}
          </p>
        )}

        {/* Meta Info Row */}
        <div className={styles.metaRow}>
          {/* Due Date */}
          <div className={`${styles.metaItem} ${dueDateInfo.class}`}>
            <DueDateIcon className={styles.metaIcon} />
            <span>{dueDateInfo.text}</span>
          </div>

          {/* Status */}
          <div className={`${styles.metaItem} ${statusInfo.class}`}>
            <StatusIcon className={styles.metaIcon} />
            <span>{statusInfo.text}</span>
          </div>

          {/* Assignee */}
          {item.assignedTo && (
            <div className={styles.metaItem}>
              <FaUser className={styles.metaIcon} />
              <span>
                {item.assignedTo.firstName} {item.assignedTo.lastName?.charAt(0)}.
              </span>
            </div>
          )}

          {/* Event */}
          {item.event && (
            <div className={styles.metaItem}>
              <FaFutbol className={styles.metaIcon} />
              <span className={styles.eventName}>{item.event.title}</span>
            </div>
          )}

          {/* Comments */}
          {commentCount > 0 && (
            <div className={styles.metaItem}>
              <FaComments className={styles.metaIcon} />
              <span>{commentCount}</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className={styles.progressSection}>
          <div className={styles.progressTrack}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${progress}%` }}
              aria-label={`Progress: ${progress}%`}
            />
          </div>
          <span className={styles.progressText}>{progress}%</span>
        </div>

        {/* Footer Row */}
        <div className={styles.footerRow}>
          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className={styles.tagsContainer}>
              {item.tags.slice(0, 3).map((tag, index) => (
                <span key={index} className={styles.tag}>
                  <FaTag className={styles.tagIcon} /> {tag}
                </span>
              ))}
              {item.tags.length > 3 && (
                <span className={styles.moreTagsCount}>+{item.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Mark Complete Button - available to managers and assigned users */}
          {(canManage || canComplete) && !bulkMode && item.status !== 'Done' && (
            <button
              className={styles.completeBtn}
              onClick={handleMarkComplete}
              disabled={isCompleting}
              title="Mark as Complete"
              aria-label="Mark as complete"
            >
              {isCompleting ? (
                <span className={styles.spinner}>⏳</span>
              ) : (
                <>
                  <FaCheck /> Complete
                </>
              )}
            </button>
          )}

          {/* Completed Badge */}
          {item.status === 'Done' && (
            <div className={styles.completedBadge}>
              <FaCheckCircle /> Completed
            </div>
          )}
        </div>
      </div>

      {/* Completion Animation Overlay */}
      {showCompletedAnimation && (
        <div className={styles.completionOverlay}>
          <div className={styles.completionIcon}>
            <FaCheckCircle />
          </div>
        </div>
      )}
    </div>
  );
};

TeamItemCard.propTypes = {
  item: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onMarkComplete: PropTypes.func,
  onQuickView: PropTypes.func,
  canManage: PropTypes.bool,
  canComplete: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  bulkMode: PropTypes.bool,
  urgencyLevel: PropTypes.string,
  commentCount: PropTypes.number
};

export default TeamItemCard;
